import React, { useEffect, useState } from "react";
import Container from "../../common/components/Container/Container";
import Breadcrumb from "../../common/components/Breadcrumb/Breadcrumb";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import i18n from "../../internationalization/i18n";
import { Button, Modal, Radio, Select, Space } from "antd";
import {
	RightCircleOutlined,
	RedoOutlined,
	CloseOutlined
} from "@ant-design/icons";
import {
	useStartChargerMutation,
	useRebootChargerMutation,
	useStopChargerMutation,
	useGetUserInfoQuery,
	useGetUserTransactionsQuery, chargersApi,
} from "services/chargersService";
import { useGetUserBillingQuery, userApi } from "services/userService"
import { useDispatch, useSelector } from "react-redux";
import { rootReducerType } from "../../redux/combineReducers";
import useWindowDimensions, { counterCommonItemsArrays, decodeJWT, getDataToDisplay } from "../../common/functions";
import InfoSection from "./components/InfoSection/InfoSection";
import "./User.less";
import UserTransactionsTable from "./components/userTransactions/UserTransactionsTable";
import UserBillingTable from "./components/userBilling/userBilling";
import { capitalizeFirstLetter, emailToUsername } from "common/utils";
import Notification from "common/components/NotificationMessage/Notification";
import { UserTransactionItem } from "services/utils/interfaces";

type ChargerParams = {
	chargerId?: string;
	userId: string;
};

interface ILocation {
	locationIdStationId: string;
	status: string;
	userId: string;
}

export type UserTransactions = Array<UserTransactionItem>;

const Users: React.FC<{ shouldRefresh: boolean }> = ({ shouldRefresh }): React.ReactElement => {
	const navigate = useNavigate();
	const location = useLocation();

	const { width } = useWindowDimensions();

	const { userId: chargerId } = useParams<keyof ChargerParams>() as ChargerParams;
	const { status, locationIdStationId, userId } = location.state as ILocation;

	const [startCharger, { isSuccess: startSuccess, data: startData }] = useStartChargerMutation();
	const [rebootCharger, { isSuccess: rebootSuccess, data: rebootData }] = useRebootChargerMutation();
	const [stopCharger, { isSuccess: stopSuccess, data: stopData }] = useStopChargerMutation();

	const { isLoggedIn } = useSelector((state: rootReducerType) => state.authenticationReducer);

	const [limit] = useState<number>(10);
	const [limitBilling] = useState<number>(10);
	const [offset, setOffset] = useState<number>(0);
	const [offsetBilling, setOffsetBilling] = useState<number>(0);
	const [previousOffset, setPreviousOffset] = useState<number>(offset);
	const [previousOffsetBilling, setPreviousOffsetBilling] = useState<number>(offset);
	const [sort, setSort] = useState<string | null>(null);
	const [sortBilling, setSortBilling] = useState<string | null>(null);

	const [userTransactions, setUserTransactions] = useState<UserTransactions>([]);
	const [userBilling, setUserBilling] = useState<UserTransactions>([]);

	const { data: chargerInfo, refetch: refetchChargerInfo } = useGetUserInfoQuery({
		userName: decodeJWT(isLoggedIn).email,
		locationIdStationId,
		chargerId: chargerId
	}, { skip: locationIdStationId && chargerId ? false : true });

	const userInfo = {
		userName: "Mariah Roberts",
		userId: "56",
		group: "Fleet",
		pricingPlan: "Corporate A",
		expiryDate: "2022-12-31",
		sinceThisMonth: "$187.5",
	};

	let sortBy: string | undefined = undefined;
	const { data, refetch: refetchChargerTransactions } = useGetUserTransactionsQuery({
		userName: decodeJWT(isLoggedIn).email,
		locationIdStationId,
		limit: String(limit),
		sortBy: sort || undefined,
		offset: String(offset < 0 ? 0 : offset),
		chargerId: chargerId
	}, { skip: locationIdStationId && chargerId ? false : true });

	const { data: billData, refetch: billRefetch } = useGetUserBillingQuery({
		// userName: decodeJWT(isLoggedIn).email,
		locationIdStationId,
		limit: String(limitBilling),
		sortBy: sortBilling || undefined,
		offset: String(offsetBilling < 0 ? 0 : offsetBilling),
		userId: userId
	}, { skip: locationIdStationId && userId ? false : true });

	const dispatch = useDispatch();

	useEffect(() => {
		if (offsetBilling < previousOffsetBilling) return;
		if (offsetBilling === previousOffsetBilling) {
			sortBy = sort || undefined;
			const tempOffset = offsetBilling < 0 ? 0 : offsetBilling;
			setOffset(tempOffset);
			setPreviousOffsetBilling(tempOffset);
			if (locationIdStationId && userId) {
				dispatch(
					userApi.endpoints.getUserBilling.initiate(
						{
							// userName: decodeJWT(isLoggedIn).email,
							locationIdStationId,
							limit: String(limitBilling),
							offset: String(tempOffset),
							sortBy: sort ? sort : undefined,
							userId: userId
						},
						{ subscribe: false, forceRefetch: true }
					)
				);
			}
		}
	}, [offsetBilling]);

	useEffect(() => {
		if (offset < previousOffset) return;
		if (offset === previousOffset) {
			sortBy = sort || undefined;
			const tempOffset = offset < 0 ? 0 : offset;
			setOffset(tempOffset);
			setPreviousOffset(tempOffset);
			if (locationIdStationId && chargerId) {
				dispatch(
					chargersApi.endpoints.getUserTransactions.initiate(
						{
							userName: decodeJWT(isLoggedIn).email,
							locationIdStationId,
							limit: String(limit),
							offset: String(tempOffset),
							sortBy: sort ? sort : undefined,
							chargerId: chargerId
						},
						{ subscribe: false, forceRefetch: true }
					)
				);
			}
		}
	}, [offset]);

	useEffect(() => {
		const tempOffset = offsetBilling <= 0 ? offsetBilling - 1 : 0;
		setOffsetBilling(tempOffset);
		setPreviousOffsetBilling(tempOffset);
	}, [sortBilling]);

	useEffect(() => {
		const tempOffset = offset <= 0 ? offset - 1 : 0;
		setOffset(tempOffset);
		setPreviousOffset(tempOffset);
	}, [sort]);

	useEffect(() => {
		if (!billData?.items) return;

		const tempOffset = offsetBilling < 0 ? 0 : offsetBilling;
		setOffsetBilling(tempOffset);
		setPreviousOffsetBilling(tempOffset);

		const counterCommonItems = counterCommonItemsArrays(billData.items, userBilling);

		const newItems = JSON.parse(JSON.stringify(
			offsetBilling <= 0
				? [...billData.items]
				: (counterCommonItems === 0
					? [...userBilling, ...billData.items]
					: [...userBilling]
				)));
		setUserBilling(newItems);
		setPreviousOffsetBilling(offsetBilling);
	}, [JSON.stringify(billData?.items), offsetBilling]);

	useEffect(() => {
		if (!data?.items) return;

		const tempOffset = offset < 0 ? 0 : offset;
		setOffset(tempOffset);
		setPreviousOffset(tempOffset);

		const counterCommonItems = counterCommonItemsArrays(data.items, userTransactions);

		const newItems = JSON.parse(JSON.stringify(
			offset <= 0
				? [...data.items]
				: (counterCommonItems === 0
					? [...userTransactions, ...data.items]
					: [...userTransactions]
				)));
		setUserTransactions(newItems);
		setPreviousOffset(offset);
	}, [JSON.stringify(data?.items), offset]);

	useEffect(() => {
		if (locationIdStationId && chargerId) {
			refetchChargerInfo();
			refetchChargerTransactions();
		}
	}, [shouldRefresh]);

	useEffect(() => {
		if (locationIdStationId && userId) {
			billRefetch();
		}
	}, [shouldRefresh]);

	const userName = decodeJWT(isLoggedIn).email;

	const breadcrumbItems = [
		{ label: i18n.t("breadcrumbs.user.users"), route: "/users" },
		{ label: chargerId || "" },
	];

	const executeFunction = (action: string) => {
		if (!chargerId || !locationIdStationId) {
			return null;
		}

		switch (action) {
			case "start": {
				startCharger({
					userName: emailToUsername(userName),
					locationIdStationId,
					chargerId: chargerId
				});
				break;
			}
			case "reboot": {
				rebootCharger({
					userName: emailToUsername(userName),
					locationIdStationId,
					chargerId: chargerId
				});
				break;
			}
			case "stop": {
				stopCharger({
					userName: emailToUsername(userName),
					locationIdStationId,
					chargerId: chargerId
				});
				break;
			}
			default: break;
		}
	};

	const popUpModal = (action: string) => {

		const content = <div className="custom-content">
			{action === "deactivate" ? <RedoOutlined style={{ color: "orange", transform: "scale(2.5)" }} /> :
				action === "activate" ? <RightCircleOutlined style={{ color: "green", transform: "scale(2.5)" }} /> :
					action === "delete" ? <CloseOutlined style={{ color: "red", transform: "scale(2.5)" }} /> :
						null
			}
			<div className="content-title">{i18n.t("user.popUpTitle", { action: action }).charAt(0).toUpperCase() + i18n.t("user.popUpTitle", { action: action }).slice(1)}</div>
			<div className="content-description">{i18n.t("user.popUpMessage", { action: action })}</div>
		</div>;

		Modal.confirm({
			className: "customModal",
			content: content,
			centered: true,
			okText: i18n.t("constants.confirm"),
			onOk: () => executeFunction(action),
			cancelText: i18n.t("constants.cancel")
		});
	};

	useEffect(() => {
		if (startSuccess)
			Notification({
				type: startData?.success ? "success" : "warning",
				message: i18n.t(startData?.success ? "charger.successModalTitle" : "charger.warningModalTitle"),
				description: startData?.success ? i18n.t("charger.successModalDesc", { action: "start" }) : capitalizeFirstLetter(startData?.message || "")
			});
	}, [startSuccess]);

	useEffect(() => {
		if (stopSuccess)
			Notification({
				type: stopData?.success ? "success" : "warning",
				message: i18n.t(stopData?.success ? "charger.successModalTitle" : "charger.warningModalTitle"),
				description: stopData?.success ? i18n.t("charger.successModalDesc", { action: "stop" }) : capitalizeFirstLetter(stopData?.message || "")
			});
	}, [stopSuccess]);

	useEffect(() => {
		if (rebootSuccess)
			Notification({
				type: rebootData?.success ? "success" : "warning",
				message: i18n.t(rebootData?.success ? "charger.successModalTitle" : "charger.warningModalTitle"),
				description: rebootData?.success ? i18n.t("charger.successModalDesc", { action: "reboot" }) : capitalizeFirstLetter(rebootData?.message || "")
			});
	}, [rebootSuccess]);

	const [isTable, setIsTable] = useState<string>('transactions')

	const handleSizeChange = (e) => {
		setIsTable(e.target.value)
	}

	return width > 768 ? (
		<Container
			backButtonAction={() => navigate("/users")}
			styleNoFlex={true}
			loadingPageName={`${i18n.t("charger.charger")} ${chargerId}`}
			subTitle={<Breadcrumb items={breadcrumbItems} />}
			actionsRight={<div style={{ marginTop: "16px" }}>
				<Button className="charger-button" onClick={() => popUpModal("activate")}>
					<RightCircleOutlined style={{ color: "green" }} />{i18n.t("user.activateUser")}
				</Button>
				<Button className="charger-button" onClick={() => popUpModal("deactivate")}>
					<RedoOutlined style={{ color: "orange" }} />{i18n.t("user.deactivateUser")}
				</Button>
				<Button className="charger-button" onClick={() => popUpModal("delete")}>
					<CloseOutlined style={{ color: "red" }} />{i18n.t("user.deleteUser")}
				</Button>
			</div>}
		>
			<div style={{ height: "100%", width: "100%", padding: "0 10px" }}>
				<div style={{ height: "100%", width: "100%" }}>
					{chargerInfo && chargerId ? (<section className="charger-container-border">
						<InfoSection
							data={chargerInfo}
							shouldRefresh={shouldRefresh}
							chargerId={chargerId}
							status={'back'} />
					</section>) : null
					}
					<Radio.Group value={isTable} onChange={handleSizeChange}>
						<Radio.Button value="transactions">Transactions</Radio.Button>
						<Radio.Button value="billing">Billing</Radio.Button>
					</Radio.Group>

					{isTable === "transactions" && userTransactions && chargerId ?
						(<div className="charger-transactions-table1" style={{ backgroundColor: "white", padding: '10px' }}>
							<UserTransactionsTable
								limit={limit}
								offset={offset < 0 ? 0 : offset}
								setOffset={setOffset}
								length={userTransactions.length + ((data?.items.length || 0) < limit ? 0 : 1)}
								data={getDataToDisplay(userTransactions, limit, offset)}
								setNewSort={setSort}
							/>
						</div>) : null
					}
					{isTable === "billing" && userBilling && userId ?
						(<div className="charger-transactions-table1" style={{ backgroundColor: "white", padding: '10px' }}>
							<UserBillingTable
								limit={limitBilling}
								offset={offsetBilling < 0 ? 0 : offsetBilling}
								setOffset={setOffsetBilling}
								length={userBilling.length + ((data?.items.length || 0) < limitBilling ? 0 : 1)}
								data={getDataToDisplay(userBilling, limitBilling, offsetBilling)}
								setNewSort={setSortBilling}
							/>
						</div>) : null
					}
				</div>
			</div>
		</Container>) : (
		<Container
			backButtonAction={() => navigate("/users")}
			loadingPageName={`User ${chargerId}`}
			subTitle={<Breadcrumb items={breadcrumbItems} />}
		>
			<div className="charger">
				<Button className="charger-button" onClick={() => popUpModal("start")}>
					<RightCircleOutlined style={{ color: "green" }} />{i18n.t("charger.startCharger")}
				</Button>
				<Button className="charger-button" onClick={() => popUpModal("reboot")}>
					<RedoOutlined style={{ color: "orange" }} />{i18n.t("charger.rebootCharger")}
				</Button>
				<Button className="charger-button" onClick={() => popUpModal("stop")}>
					<CloseOutlined style={{ color: "red" }} />{i18n.t("charger.stopCharger")}
				</Button>
				{
					userInfo && userInfo && chargerId && status ? (
						<div className="charger-container-border">
							<InfoSection
								data={userInfo}
								shouldRefresh={shouldRefresh}
								chargerId={chargerId}
								status={status} />
						</div>
					)
						: null
				}
				<div style={{ height: "100%", width: "90vw", marginTop: "-10px" }}>
					{
						userTransactions && chargerId && status ? (
							<div className="charger-transactions-table">
								<UserTransactionsTable
									limit={limit}
									offset={offset < 0 ? 0 : offset}
									setOffset={setOffset}
									length={userTransactions.length + ((data?.items.length || 0) < limit ? 0 : 1)}
									data={getDataToDisplay(userTransactions, limit, offset)}
									setNewSort={setSort}
								/>
							</div>
						)
							: null
					}
				</div>
			</div>
		</Container>
	);
};

export default Users;
